import { useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'

import type { Gebied } from '@models/Gebied'
import type { GroupedOption } from '@models/GroupedOption'

import { SelectObject } from '../../../core/components/SelectObject'
import type { Projectdossier } from '../../../projectdossier/types/Projectdossier'
import type { Projectmelding } from '../../../projectmelding/types/Projectmelding'

interface Props {
  projectmelding?: Projectmelding
  gebieden: Gebied[]
  organisaties: GroupedOption[]
  isLoadingOrganisaties: boolean
  combi?: string
  // The organisaties depend on the chosen contractgebied, so the dialog needs to know it
  onGebiedscodeChange: (gebiedscode?: string) => void
  onSubmit: (projectdossier: Partial<Projectdossier>) => void
  onClose: () => void
}

export const CreateProjectdossierForm = ({
  projectmelding,
  gebieden,
  organisaties,
  isLoadingOrganisaties,
  combi,
  onGebiedscodeChange,
  onSubmit,
  onClose,
}: Props) => {
  const [name, setName] = useState(projectmelding?.projectName ?? '')
  const [referenceId, setReferenceId] = useState('')
  const [indienerNaam, setIndienerNaam] = useState(() => {
    if (!projectmelding) return ''

    const { gender, initials, middleName, lastName } = projectmelding

    return `${gender} ${initials} ${middleName} ${lastName}`
  })
  const [indienerOrg, setIndienerOrg] = useState(
    projectmelding?.companyName ?? '',
  )
  const [location, setLocation] = useState(projectmelding?.description ?? '')
  const [gebiedscode, setGebiedscode] = useState(
    projectmelding?.contractGebiedCode,
  )
  const [errors, setErrors] = useState({})
  // Undefined until the user chooses, so the combi is selected by default once
  // the organisaties of the contractgebied arrive
  const [involvedParties, setInvolvedParties] = useState<GroupedOption[]>()

  // Derived, because the gebieden can arrive after the form is opened
  const contractgebied = gebieden.find((g) => g.code === gebiedscode) || null
  const selectedParties =
    involvedParties ?? organisaties.filter((x) => x.label === combi)

  const validate = () => {
    const nameError = !name
    const indienerNaamError = !indienerNaam
    const indienerOrgError = !indienerOrg
    const locationError = !location
    const contractgebiedError = !contractgebied

    setErrors({
      nameError,
      indienerNaamError,
      indienerOrgError,
      locationError,
      contractgebiedError,
    })

    return !(
      nameError ||
      indienerNaamError ||
      indienerOrgError ||
      contractgebiedError ||
      locationError
    )
  }

  const handleSubmitClick = () => {
    if (validate()) {
      const body: any = {
        naam: name,
        externeReferentie: referenceId,
        locatieOmschrijving: location,
        organisaties: selectedParties.map((x) => x.label),
        gebiedscode: contractgebied!.code,
        indiener: {
          naam: indienerNaam,
          organisatie: indienerOrg,
        },
      }

      if (projectmelding?.id) {
        body.projectmeldingId = projectmelding.id
      }

      onSubmit(body)
    }
  }

  const handleContractgebiedChange = (gebied: Gebied | null) => {
    setGebiedscode(gebied?.code)
    // The organisaties change with the contractgebied, so start over with the combi
    setInvolvedParties(undefined)
    onGebiedscodeChange(gebied?.code)
  }

  const handleInvolvedPartiesChange = (value) => {
    const partyToKeep = selectedParties.find((x) => x.label === combi)

    if (value.length === 0 && partyToKeep) {
      setInvolvedParties([partyToKeep])
    } else {
      setInvolvedParties(value)
    }
  }

  return (
    <>
      <Dialog open fullWidth maxWidth={'md'}>
        <DialogTitle>Aanmaken projectdossier</DialogTitle>
        <DialogContent>
          <Box
            sx={{ width: '95%', marginLeft: 'auto', marginRight: 'auto', p: 1 }}
          >
            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={name}
                label={'Dossiernaam'}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                onChange={(e) => setName(e.target.value)}
                error={errors['nameError']}
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={referenceId}
                label={'Externe referentie'}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                onChange={(e) => setReferenceId(e.target.value)}
                error={errors['referenceIdError']}
              />
            </FormControl>

            {projectmelding?.id && (
              <FormControl sx={{ width: '100%', p: 1 }}>
                <TextField
                  variant="standard"
                  value={projectmelding.id}
                  label={'Projectmelding id'}
                  disabled
                />
              </FormControl>
            )}

            <FormControl sx={{ width: '100%', p: 1 }}>
              <SelectObject
                disabled={!!projectmelding?.id}
                options={gebieden}
                value={contractgebied}
                onChange={handleContractgebiedChange}
                label="Contractgebied"
                error={errors['contractgebiedError']}
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={location}
                multiline
                rows={3}
                label={`Beschrijving (${location.length}/500)`}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                onChange={(e) => setLocation(e.target.value)}
                error={errors['locationError']}
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <SelectObject
                multiple
                disabled={!contractgebied || isLoadingOrganisaties}
                options={organisaties}
                value={selectedParties}
                onChange={handleInvolvedPartiesChange}
                label="Betrokken partijen"
                helperText="De combi geldt altijd als betrokken partij"
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={indienerNaam}
                label={'Indiener'}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                onChange={(e) => setIndienerNaam(e.target.value)}
                error={errors['indienerNaamError']}
              />
            </FormControl>

            <FormControl sx={{ width: '100%', p: 1 }}>
              <TextField
                variant="standard"
                data-testid="inputField"
                value={indienerOrg}
                label={'Indienende organisatie'}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                onChange={(e) => setIndienerOrg(e.target.value)}
                error={errors['indienerOrgError']}
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose}>
            Annuleren
          </Button>
          <Button color="primary" onClick={handleSubmitClick}>
            Aanmaken
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
